import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Image,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  FormErrorMessage,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { supabase } from '../lib/supabase';
import { getStoredAuthType, clearStoredAuthType } from '../lib/captureInviteHash';

export default function SetPassword() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const toast = useToast();

  useEffect(() => {
    async function ensureSession() {
      console.log('[SetPassword] ensureSession started');
      console.log('[SetPassword] current hash:', window.location.hash?.slice(0, 80));

      const stored = getStoredAuthType();
      console.log('[SetPassword] stored auth type:', stored?.type ?? 'none');

      if (stored?.access_token && stored?.refresh_token) {
        console.log('[SetPassword] restoring session from sessionStorage...');
        const { error: sessErr } = await supabase.auth.setSession({
          access_token: stored.access_token,
          refresh_token: stored.refresh_token,
        });
        console.log('[SetPassword] setSession result:', sessErr ? sessErr.message : 'ok');
        clearStoredAuthType();
      }

      console.log('[SetPassword] calling getUser()...');
      const { data: { user }, error } = await supabase.auth.getUser();
      console.log('[SetPassword] getUser result:', user ? user.email : 'null', error?.message ?? '');
      console.log('[SetPassword] user_metadata:', JSON.stringify(user?.user_metadata ?? {}));

      if (!user) {
        console.log('[SetPassword] no user — redirecting to /login');
        window.location.href = '/login';
        return;
      }
      console.log('[SetPassword] user found — showing form');
      setReady(true);
    }
    ensureSession();
  }, []);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const namesMissing = firstName.trim().length === 0 || lastName.trim().length === 0;
  const canSubmit = password.length >= 8 && password === confirm && !namesMissing;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setLoading(true);
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const { error } = await supabase.auth.updateUser({
        password,
        data: {
          needs_password_setup: false,
          full_name: fullName,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
        },
      });
      if (error) throw error;

      clearStoredAuthType();
      toast({
        title: 'Password set successfully',
        description: 'You can now sign in with your new password.',
        status: 'success',
        duration: 5000,
      });
      window.location.href = '/';
    } catch (error) {
      toast({
        title: 'Failed to set password',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }

  if (!ready) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="brand.400" />
      </Center>
    );
  }

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, amber.50, white, orange.50)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      <Box
        bg="white"
        p={{ base: 8, md: 10 }}
        borderRadius="2xl"
        boxShadow="2xl"
        borderWidth={1}
        borderColor="gray.100"
        maxW="md"
        w="full"
      >
        <VStack spacing={6} align="stretch">
          <VStack spacing={3}>
            <Image
              src="/images/mesa-logo-for-light-bg.png"
              alt="Mesa Group Consulting"
              maxW="286px"
            />
            <Heading fontSize="xl" textAlign="center" color="gray.900">
              Set Your Password
            </Heading>
            <Text textAlign="center" color="gray.600" fontSize="sm">
              Choose a password for your account. You'll use this to sign in.
            </Text>
          </VStack>

          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <Box display="flex" gap={3} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                    First Name
                  </FormLabel>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    size="lg"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{
                      borderColor: 'brand.400',
                      boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.1)',
                    }}
                    autoComplete="given-name"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                    Last Name
                  </FormLabel>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    size="lg"
                    borderColor="gray.300"
                    _hover={{ borderColor: 'gray.400' }}
                    _focus={{
                      borderColor: 'brand.400',
                      boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.1)',
                    }}
                    autoComplete="family-name"
                  />
                </FormControl>
              </Box>

              <FormControl isRequired isInvalid={tooShort}>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  New Password
                </FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  size="lg"
                  borderColor="gray.300"
                  _hover={{ borderColor: 'gray.400' }}
                  _focus={{
                    borderColor: 'brand.400',
                    boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.1)',
                  }}
                />
                {tooShort && (
                  <FormErrorMessage>Password must be at least 8 characters.</FormErrorMessage>
                )}
              </FormControl>

              <FormControl isRequired isInvalid={mismatch}>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  Confirm Password
                </FormLabel>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter your password"
                  size="lg"
                  borderColor="gray.300"
                  _hover={{ borderColor: 'gray.400' }}
                  _focus={{
                    borderColor: 'brand.400',
                    boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.1)',
                  }}
                />
                {mismatch && (
                  <FormErrorMessage>Passwords do not match.</FormErrorMessage>
                )}
              </FormControl>

              <Button
                type="submit"
                width="100%"
                size="lg"
                bg="brand.400"
                color="gray.900"
                fontWeight="semibold"
                _hover={{
                  bg: 'brand.500',
                  transform: 'translateY(-2px)',
                  shadow: 'lg',
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
                isLoading={loading}
                isDisabled={!canSubmit}
                mt={2}
                transition="all 0.2s"
              >
                Set Password
              </Button>
            </VStack>
          </form>

          <Text fontSize="xs" color="gray.400" textAlign="center" mt={4}>
            Powered by Mesa Group Consulting
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}
